import React, { useCallback, useEffect, useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Button,
  IconButton,
  Chip,
  Card,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  Grid,
  ToggleButton,
  InputAdornment,
  TextField,
  FormControl,
  Select,
  InputLabel,
  MenuItem,
  OutlinedInput,
} from "@mui/material";
import { createTheme, ThemeProvider, styled } from "@mui/material/styles";
import {
  Search as SearchIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Today as TodayIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  EventRepeat as EventRepeatIcon, // Reschedule Icon
  InfoOutlined as InfoOutlinedIcon,
  AccessTime as AccessTimeIcon,
} from "@mui/icons-material";
import {
  listAppointments,
  processListingAppointment,
  saveAppointment,
  listMonthAppointmentIndicators,
  cancelTourBooking,
} from "../../../services/listingsServices/listing.service";
import LoadingOverlay from "../../../components/overlays/loadingOverlay";
import ConfirmationModal from "../../../components/modals/confirmation";
import { SuggestedDatesSelector } from "../listingView";
import { titleCase } from "../../../util/util";

// Define a custom theme for Material-UI
const theme = createTheme({
  typography: {
    fontFamily: ["Inter", "sans-serif"].join(","),
  },
  palette: {
    primary: {
      main: "#037171",
    },
    secondary: {
      main: "#f50057",
    },
    background: {
      default: "#f4f6f8",
      paper: "#ffffff",
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: "none",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 4,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.05)",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: "none",
          "&.Mui-selected": {
            backgroundColor: "rgba(0, 0, 0, 0.08)", // A subtle gray for selected toggle
          },
        },
      },
    },
  },
});

// Styled components for layout
const Container = styled(Box)(({ theme }) => ({
  display: "flex",
  height: "100vh",
  backgroundColor: theme.palette.background.default,
}));

const Sidebar = styled(Paper)(({ theme }) => ({
  width: 280,
  flexShrink: 0,
  backgroundColor: theme.palette.background.paper,
  borderRight: `1px solid ${theme.palette.divider}`,
  overflowY: "auto",
  maxHeight: "calc(100vh - 110px)",
  "&::-webkit-scrollbar": {
    width: "0px",
    background: "transparent",
  },
  padding: theme.spacing(2),
  [theme.breakpoints.down("md")]: {
    display: "none", // Hide sidebar on smaller screens
  },
}));

const MainContentArea = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1),
  },
}));

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  // zIndex: theme.zIndex.drawer + 1,
  backgroundColor: theme.palette.background.paper,
  boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.04)",
  color: theme.palette.text.primary,
}));

const CalendarGrid = styled(Grid)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 8,
  padding: theme.spacing(1),
  marginBottom: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
}));

const CalendarDay = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== "isSelected" &&
    prop !== "isToday" &&
    prop !== "isGrayedOut" &&
    prop !== "isPastDay" &&
    prop !== "hasAppointment",
})(
  ({ theme, isSelected, isToday, isGrayedOut, isPastDay, hasAppointment }) => ({
    width: 32,
    height: 32,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "50%",
    fontWeight: isSelected ? "bold" : "normal",
    backgroundColor: isSelected ? theme.palette.primary.main : "transparent",
    color: isSelected
      ? theme.palette.common.white
      : isGrayedOut || isPastDay
        ? theme.palette.text.secondary
        : theme.palette.text.primary,
    cursor: isGrayedOut || isPastDay ? "not-allowed" : "pointer", // Disable cursor for grayed out/past days
    position: "relative", // Added for the appointment dot
    "&:hover": {
      backgroundColor: isSelected
        ? theme.palette.primary.dark
        : isGrayedOut || isPastDay
          ? "transparent"
          : theme.palette.action.hover,
    },
    ...(isToday &&
      !isSelected && {
        border: `2px solid ${theme.palette.primary.main}`,
      }),
  }),
);

// Reusable Booking Card Component
const BookingCard = ({ booking, handleAppointmentActions }) => {
  const appointmentDate = new Date(booking?.appointmentDate);

  const handleApprove = () => {
    handleAppointmentActions(booking, "approve");
  };

  const handleRejectCancel = () => {
    const op = booking?.isOwner ? "cancel" : "reject";
    handleAppointmentActions(booking, op);
  };

  const handleReschedule = () => {
    handleAppointmentActions(booking, "reschedule");
  };

  const formatTime = () => {
    return appointmentDate.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Determine chip color for status_cd
  const getStatusChipColor = (status) => {
    switch (status) {
      case "Approved":
        return { bgcolor: "#e8f5e9", color: "#1b5e20" }; // Greenish
      case "Pending":
        return { bgcolor: "#fff3e0", color: "#e65100" }; // Orangish
      case "Rejected":
        return { bgcolor: "#ffebee", color: "#c62828" }; // Reddish
      default:
        return { bgcolor: "#e0e0e0", color: "#424242" }; // Gray
    }
  };

  const statusColors = getStatusChipColor(booking?.status);

  return (
    <Card
      sx={{
        display: "flex",
        alignItems: "center",
        p: 2,
        mb: 2,
        borderLeft: `5px solid ${statusColors?.color}`,
        position: "relative",
        overflow: "hidden",
        transition: "box-shadow 0.3s ease-in-out",
        "&:hover": {
          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
        },
      }}
    >
      <Box sx={{ flexShrink: 0, mr: 3, textAlign: "center", minWidth: 80 }}>
        <Typography variant="subtitle2" color="text.secondary">
          {formatTime(appointmentDate)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {appointmentDate.toLocaleDateString("en-US", {
            day: "numeric",
            month: "short",
          })}
        </Typography>
        {booking.duration && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mt: 0.5,
              color: theme.palette.text.secondary,
            }}
          >
            <AccessTimeIcon sx={{ fontSize: 16, mr: 0.5 }} />
            <Typography variant="caption">{booking.duration} min</Typography>
          </Box>
        )}
      </Box>

      <Divider orientation="vertical" flexItem sx={{ mr: 3 }} />

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1 }}>
        <Chip
          label={booking.status}
          size="medium"
          sx={{ ...statusColors, fontWeight: "medium" }}
        />
        <Chip
          label={booking?.isOwner ? "My Booking" : "Tour Appointment"}
          size="medium"
          sx={{ ...statusColors, fontWeight: "medium" }}
        />
      </Box>
      <Box sx={{ flexGrow: 1 }}>
        <Typography
          variant="h6"
          component="div"
          sx={{ fontWeight: "bold", mb: 0.5 }}
        >
          Listing: {booking.listingName}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
          Created by:{" "}
          <span style={{ fontWeight: "bold" }}>{booking?.person?.name}</span>
        </Typography>
        {booking?.person?.email && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            Email:{" "}
            <span style={{ fontWeight: "bold" }}>{booking.person.email}</span>
          </Typography>
        )}
        {booking?.person?.phone && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            Phone:{" "}
            <span style={{ fontWeight: "bold" }}>{booking.person.phone}</span>
          </Typography>
        )}
        {booking.reason && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "text.secondary",
            }}
          >
            <InfoOutlinedIcon fontSize="small" sx={{ mr: 0.5 }} />
            <Typography variant="body2" fontStyle="italic">
              {booking.reason}
            </Typography>
          </Box>
        )}
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: 3 }}>
        {/* Action Buttons */}
        {booking?.status === "Pending" && !booking?.isPast && (
          <>
            {!booking?.isOwner && (
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={<CheckIcon />}
                onClick={handleApprove}
              >
                Approve
              </Button>
            )}
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<EventRepeatIcon />}
              onClick={handleReschedule}
            >
              Reschedule
            </Button>
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<CloseIcon />}
              onClick={handleRejectCancel}
            >
              {booking?.isOwner ? "Cancel" : "Reject"}
            </Button>
          </>
        )}
        {booking?.status === "Approved" && !booking?.isPast && (
          <Button
            variant="outlined"
            color="error"
            size="small"
            startIcon={<CloseIcon />}
            onClick={handleRejectCancel}
          >
            Cancel
          </Button>
        )}
      </Box>
    </Card>
  );
};

const statusOptions = [
  {
    value: "approved",
    label: "Approved",
  },
  {
    value: "pending",
    label: "Pending",
  },
  {
    value: "all",
    label: "All",
  },
];

function ListingAppointments() {
  const [currentDisplayDate, setCurrentDisplayDate] = useState(new Date());
  const [selectedDayInCalendar, setSelectedDayInCalendar] = useState(
    new Date(),
  );
  const [showFutureBookingsOnly, setShowFutureBookingsOnly] = useState(true);

  const [showAllTodayEvents, setShowAllTodayEvents] = useState(false);
  const [showAllUpcomingEvents, setShowAllUpcomingEvents] = useState(false);

  const [sideBarEvents, setSidebarEvents] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [filteredAppointments, setFilteredAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterOption, setFilterOption] = useState("all");
  const [confirmType, setConfirmType] = useState("");
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [reason, setReason] = useState("");
  const [selectedDates, setSelectedDates] = useState([]);
  const [monthAppsMap, setMonthAppsMap] = useState(new Map());

  const populateSideBarEvents = useCallback((data) => {
    let events = {
      now: [],
      upcoming: [],
    };
    data?.forEach((ap) => {
      const start = new Date(ap.appointmentDate);
      const end = new Date(ap.expectedEndTime);
      const now = new Date();
      const event = {
        name: ap.listingName,
        time: `${start.toLocaleTimeString()} - ${new Date(
          ap.expectedEndTime,
        ).toLocaleTimeString()}`,
      };
      if (now >= start && now < end) {
        events.now = [...events.now, event];
      } else if (now < start) {
        events.upcoming = [...events.upcoming, event];
      }
    });
    setSidebarEvents(events);
  }, []);

  const updateMonthAppointmentIndicators = useCallback((date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    listMonthAppointmentIndicators(year, month).then((data) => {
      const appsMap = new Map();
      data?.forEach((ap) => {
        const date = new Date(ap?.appointmentDate).getDate();
        appsMap.set(date, true);
      });
      setMonthAppsMap(appsMap);
    });
  }, []);

  const fetchAppointments = useCallback(() => {
    setLoading(true);
    listAppointments(selectedDayInCalendar).then((data) => {
      setAppointments(data);
      setFilteredAppointments(data);
      populateSideBarEvents(data);
      setLoading(false);
    });
  }, [selectedDayInCalendar, populateSideBarEvents]);

  useEffect(() => {
    fetchAppointments();
  }, [selectedDayInCalendar, fetchAppointments]);

  useEffect(() => {
    if (!searchTerm) {
      setFilteredAppointments(appointments);
      return;
    }
    const timer = setTimeout(() => {
      const filtered = appointments.filter((ap) => {
        return (
          ap.status.toLowerCase().includes(searchTerm.toLowerCase()) ||
          ap.listingName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          ap?.person?.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
      });

      setFilteredAppointments(filtered);
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm, appointments]);

  useEffect(() => {
    if (filterOption === "all") {
      return setFilteredAppointments(appointments);
    }
    const filtered = appointments.filter(
      (ap) => ap.status.toLowerCase() === filterOption,
    );
    setFilteredAppointments(filtered);
  }, [filterOption, appointments]);
  useEffect(() => {
    updateMonthAppointmentIndicators(currentDisplayDate);
  }, [currentDisplayDate, updateMonthAppointmentIndicators]);

  const handlePrevMonth = () => {
    setCurrentDisplayDate(
      (prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1),
    );
  };

  const handleNextMonth = () => {
    setCurrentDisplayDate(
      (prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1),
    );
  };

  const handleGoToToday = () => {
    const today = new Date();
    setSelectedDayInCalendar(today);
    setCurrentDisplayDate(today);
    setAppointments([]);
  };

  // Date selector
  const handleSidebarDayClick = (dayNum) => {
    const newSelectedDate = new Date(currentDisplayDate.setDate(dayNum));
    setSelectedDayInCalendar(newSelectedDate);
  };

  const generateCalendarDays = (date, todayDateOnly, selectedFutureOnly) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDayOfMonth = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const days = [];

    const daysInPrevMonth = new Date(year, month, 0).getDate();
    for (let i = firstDayOfMonth - 1; i >= 0; i--) {
      const dayNum = daysInPrevMonth - i;
      const dayDate = new Date(year, month - 1, dayNum);
      dayDate.setHours(0, 0, 0, 0);
      const isPast = selectedFutureOnly && dayDate < todayDateOnly;
      days.push({
        day: dayNum,
        grayedOut: true,
        isPast: isPast,
        hasAppointment: false,
      });
    }

    // Add days of the current month
    for (let i = 1; i <= daysInMonth; i++) {
      const dayDate = new Date(year, month, i);
      dayDate.setHours(0, 0, 0, 0);
      const isPast = selectedFutureOnly && dayDate < todayDateOnly;
      const hasAppointment = monthAppsMap.has(i); // Check for appointment
      days.push({
        day: i,
        grayedOut: false,
        isPast: isPast,
        hasAppointment: hasAppointment,
      });
    }

    const remainingSlots = 42 - days.length;
    for (let i = 1; i <= remainingSlots; i++) {
      const dayNum = i;
      const dayDate = new Date(year, month + 1, dayNum);
      dayDate.setHours(0, 0, 0, 0);
      const isPast = selectedFutureOnly && dayDate < todayDateOnly;
      days.push({
        day: dayNum,
        grayedOut: true,
        isPast: isPast,
        hasAppointment: false,
      });
    }

    return days;
  };

  const today = new Date();
  const todayDateOnly = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );
  const calendarDays = generateCalendarDays(
    currentDisplayDate,
    todayDateOnly,
    showFutureBookingsOnly,
  );

  const toggleShowAllTodayEvents = () => setShowAllTodayEvents((prev) => !prev);
  const toggleShowAllUpcomingEvents = () =>
    setShowAllUpcomingEvents((prev) => !prev);

  const handleToggleFutureBookings = () => {
    setShowFutureBookingsOnly((prev) => !prev);
    const filtered = appointments.filter((ap) => {
      return new Date(ap?.appointmentDate) >= today;
    });
    setFilteredAppointments(filtered);
  };
  async function handleApproveOrRejectAppointment() {
    setLoading(true);
    switch (confirmType) {
      case "approve":
        await processListingAppointment({
          appointmentId: selectedBooking?.id,
          action: "approve",
          reason: reason,
        });
        setReason("");
        break;
      case "reject":
        await processListingAppointment({
          appointmentId: selectedBooking?.id,
          action: "reject",
          reason: reason,
        });
        setReason("");
        break;
      case "reschedule":
        const data = {
          rescheduleReason: reason,
          suggestedTimes: selectedDates,
          appointmentDate: selectedDates.length > 0 ? selectedDates[0] : null,
          appointmentId: selectedBooking?.id,
          listingId: selectedBooking?.listingId,
        };
        await saveAppointment(data);
        setSelectedDates([]);
        break;
      case "cancel":
        // Add the missing cancel case
        await cancelTourBooking(selectedBooking?.id);
        break;
      default:
        break;
    }

    setConfirmType("");
    setSelectedBooking(null);
    setReason("");
    setSelectedDates([]);
    fetchAppointments();

    // Update calendar indicators after any appointment action
    updateMonthAppointmentIndicators(currentDisplayDate);
  }

  function handleAppointmentActions(row, actionType) {
    setConfirmType(actionType);
    setSelectedBooking(row);
  }

  return (
    <ThemeProvider theme={theme}>
      <Container>
        <Sidebar>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              pb: 2,
              mb: 2,
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Typography
              variant="h6"
              component="div"
              sx={{ flexGrow: 1, fontWeight: "bold" }}
            >
              Appointments
            </Typography>
          </Box>

          <CalendarGrid container spacing={0.5} sx={{ textAlign: "center" }}>
            <Grid
              item
              xs={12}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <IconButton size="small" onClick={handlePrevMonth}>
                <ChevronLeftIcon />
              </IconButton>
              <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                {currentDisplayDate.toLocaleString("en-US", { month: "short" })}{" "}
                {currentDisplayDate.getFullYear()}
              </Typography>
              <IconButton size="small" onClick={handleNextMonth}>
                <ChevronRightIcon />
              </IconButton>
            </Grid>
            <Grid item xs={12}>
              <Grid container justifyContent="space-around">
                {["S", "M", "T", "W", "T", "F", "S"].map((dayChar, index) => (
                  <Grid item key={index} sx={{ width: 32 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontWeight: "bold" }}
                    >
                      {dayChar}
                    </Typography>
                  </Grid>
                ))}
              </Grid>
            </Grid>
            {calendarDays.map((day, index) => {
              return (
                <Grid item key={index} sx={{ width: 32 }}>
                  <CalendarDay
                    isSelected={
                      !day.grayedOut &&
                      !day.isPast &&
                      day.day === selectedDayInCalendar.getDate() &&
                      currentDisplayDate.getMonth() ===
                        selectedDayInCalendar.getMonth() &&
                      currentDisplayDate.getFullYear() ===
                        selectedDayInCalendar.getFullYear()
                    }
                    isToday={
                      !day.grayedOut &&
                      !day.isPast &&
                      day.day === today.getDate() &&
                      currentDisplayDate.getMonth() === today.getMonth() &&
                      currentDisplayDate.getFullYear() ===
                        today.getFullYear() &&
                      !(
                        day.day === selectedDayInCalendar.getDate() &&
                        currentDisplayDate.getMonth() ===
                          selectedDayInCalendar.getMonth() &&
                        currentDisplayDate.getFullYear() ===
                          selectedDayInCalendar.getFullYear()
                      ) // Don't show today highlight if it's also selected
                    }
                    isGrayedOut={day.grayedOut}
                    isPastDay={day.isPast}
                    hasAppointment={day.hasAppointment}
                    onClick={() =>
                      !day.grayedOut &&
                      !day.isPast &&
                      handleSidebarDayClick(day.day)
                    }
                  >
                    <Typography variant="body2">{day.day}</Typography>
                    {day.hasAppointment && (
                      <Box
                        sx={{
                          width: 5,
                          height: 5,
                          borderRadius: "50%",
                          bgcolor: "primary.main",
                          position: "absolute",
                          bottom: 4,
                        }}
                      />
                    )}
                  </CalendarDay>
                </Grid>
              );
            })}
          </CalendarGrid>

          {/* Events List */}
          <Box sx={{ mt: 3 }}>
            <List dense>
              <ListItem disablePadding sx={{ mb: 1 }}>
                <ListItemText
                  primary={
                    <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                      All Events Today
                    </Typography>
                  }
                />
                <Button
                  size="small"
                  sx={{ textTransform: "none", fontSize: 12 }}
                  onClick={toggleShowAllTodayEvents}
                >
                  {showAllTodayEvents ? "Collapse" : "Show +"}
                </Button>
              </ListItem>
              <ListItemText
                primary={
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: "bold", mb: 1 }}
                  >
                    Ongoing
                  </Typography>
                }
              />
              {sideBarEvents?.now
                ?.slice(0, showAllTodayEvents ? sideBarEvents?.now?.length : 1)
                ?.map((event, index) => (
                  <ListItem
                    key={`today-${index}`}
                    sx={{
                      bgcolor: theme.palette.grey[100],
                      borderRadius: 1,
                      mb: 0.5,
                    }}
                  >
                    <ListItemText
                      primary={
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: "medium" }}
                        >
                          {event.name}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          {event.time}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}

              {sideBarEvents?.now?.length <= 0 && (
                <ListItemText
                  primary={
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: "normal", color: "text.secondary" }}
                    >
                      No Ongoing Events
                    </Typography>
                  }
                />
              )}

              <Divider sx={{ my: 2 }} />

              <ListItemText
                primary={
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: "bold", mb: 1 }}
                  >
                    Upcoming
                  </Typography>
                }
              />
              {sideBarEvents?.upcoming
                ?.slice(
                  0,
                  showAllUpcomingEvents ? sideBarEvents?.upcoming?.length : 3,
                )
                ?.map((event, index) => (
                  <ListItem
                    key={`upcoming-${index}`}
                    sx={{
                      bgcolor: theme.palette.grey[100],
                      borderRadius: 1,
                      mb: 0.5,
                    }}
                  >
                    <ListItemText
                      primary={
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: "medium" }}
                        >
                          {event.name}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          {event.time}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              {sideBarEvents?.upcoming?.length > 3 && (
                <Button
                  size="small"
                  sx={{ textTransform: "none", fontSize: 12, mt: 1 }}
                  onClick={toggleShowAllUpcomingEvents}
                >
                  {showAllUpcomingEvents
                    ? "Collapse"
                    : `Show all ${sideBarEvents?.upcoming?.length} events`}
                </Button>
              )}
            </List>
          </Box>
        </Sidebar>

        {/* Main Content */}
        <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
          <StyledAppBar position="static">
            <Toolbar
              sx={{
                justifyContent: "space-between",
                alignItems: "center",
                height: 70,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography
                  variant="h6"
                  component="div"
                  sx={{ fontWeight: "bold", mr: 2 }}
                >
                  Appointments
                </Typography>
                <TextField
                  variant="outlined"
                  size="small"
                  placeholder="Search appointments..."
                  sx={{
                    ml: 1,
                    minWidth: 200,
                    bgcolor: theme.palette.grey[100],
                    borderRadius: 1,
                    ".MuiOutlinedInput-notchedOutline": { border: "none" },
                    ".MuiInputBase-input": { p: "8px 12px" },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </Box>
            </Toolbar>
          </StyledAppBar>

          {/* Main Content Area */}
          {loading ? (
            <LoadingOverlay />
          ) : (
            <MainContentArea>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 3,
                }}
              >
                {/* Date Navigation */}
                <Box sx={{ display: "flex", alignItems: "center", flexDirection: { xs: "column", sm: "row" } }}>
                  <Typography variant="h5" sx={{ fontWeight: "bold", mr: 2 }}>
                    {selectedDayInCalendar.toLocaleDateString("en-US", {
                      weekday: "short",
                      month: "short",
                      day: "numeric",
                    })}
                  </Typography>

                  <Box sx={{ display: "flex", alignItems: "center", flexWrap: "nowrap" }}>
                    <IconButton
                      size="small"
                      onClick={() =>
                        setSelectedDayInCalendar((prev) => {
                          const newDate = new Date(prev);
                          newDate.setDate(prev.getDate() - 1);
                          return newDate;
                        })
                      }
                    >
                      <ChevronLeftIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      sx={{ mr: 1 }}
                      onClick={() =>
                        setSelectedDayInCalendar((prev) => {
                          const newDate = new Date(prev);
                          newDate.setDate(prev.getDate() + 1);
                          return newDate;
                        })
                      }
                    >
                      <ChevronRightIcon />
                    </IconButton>
                  </Box>
                  <Button
                    variant="outlined"
                    startIcon={<TodayIcon />}
                    sx={{
                      textTransform: "none",
                      borderColor: theme.palette.divider,
                      color: theme.palette.text.primary,
                    }}
                    onClick={handleGoToToday}
                  >
                    Today
                  </Button>
                </Box>

                {/* View Toggles & Avatars */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <FormControl
                    sx={{
                      width: "100%",
                    }}
                  >
                    <InputLabel id="filterOption">Filter</InputLabel>
                    <Select
                      defaultValue=""
                      id="filterSelector"
                      value={filterOption}
                      label="Filter"
                      sx={{
                        width: "150px",
                        height: "3.7vh",
                      }}
                      onChange={(e) => setFilterOption(e.target.value)}
                      MenuProps={{
                        style: {
                          maxHeight: "300px",
                        },
                      }}
                    >
                      {statusOptions?.length > 0 ? (
                        statusOptions?.map((item, index) => (
                          <MenuItem key={index} value={item.value}>
                            {item.label}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem sx={{ width: "100%" }}>
                          No Results Found
                        </MenuItem>
                      )}
                    </Select>
                  </FormControl>

                  <ToggleButton
                    value="check" // A value is required for ToggleButton
                    selected={showFutureBookingsOnly}
                    onChange={handleToggleFutureBookings}
                    size="small"
                    sx={{
                      textTransform: "none",
                      fontWeight: "normal",
                      backgroundColor: theme.palette.primary,
                      textWrap: "nowrap",
                    }}
                  >
                    {showFutureBookingsOnly
                      ? "Show All Appointments"
                      : "Only Show Future Appointments"}
                  </ToggleButton>
                </Box>
              </Box>

              {/* List of Bookings */}
              <Box sx={{ maxHeight: "calc(100vh - 290px)", overflowY: "auto" }}>
                {filteredAppointments?.length > 0 ? (
                  filteredAppointments.map((booking) => (
                    <BookingCard
                      key={booking.id}
                      booking={booking}
                      handleAppointmentActions={handleAppointmentActions}
                    />
                  ))
                ) : (
                  <Typography
                    variant="h6"
                    color="text.secondary"
                    sx={{ textAlign: "center", mt: 4 }}
                  >
                    No appointments for this date.
                  </Typography>
                )}
              </Box>
            </MainContentArea>
          )}
        </Box>
      </Container>
      {confirmType && (
        <ConfirmationModal
          showConfirmationModal={confirmType}
          positiveAction={handleApproveOrRejectAppointment}
          negativeAction={() => {
            setConfirmType("");
            setSelectedBooking(null);
            setReason("");
            setSelectedDates([]);
          }}
          severity={
            confirmType === "cancel" || confirmType === "reject"
              ? "error"
              : confirmType === "approve"
                ? "success"
                : "info"
          }
          title={`${titleCase(confirmType)} Booking`}
          body={"Are you sure you want to " + confirmType + " this booking?"}
          additionalContent={
            <Box sx={{ width: "100%", mt: 2 }}>
              {(confirmType === "approve" ||
                confirmType === "reject" ||
                confirmType === "reschedule") && (
                <>
                  <Typography sx={{ mb: 1 }}>
                    Reason{" "}
                    {confirmType === "reschedule" &&
                      !selectedBooking?.isOwner && (
                        <span style={{ color: "red" }}>*</span>
                      )}
                  </Typography>
                  <OutlinedInput
                    autoFocus={true}
                    multiline={true}
                    rows={2}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder={
                      confirmType === "reschedule" && !selectedBooking?.isOwner
                        ? "Reschedule reason is required..."
                        : `Add a reason why to ${confirmType} this booking...`
                    }
                    sx={{ marginBottom: "10px", width: "100%" }}
                    required={
                      confirmType === "reschedule" && !selectedBooking?.isOwner
                    }
                  />
                </>
              )}

              {confirmType === "reschedule" && (
                <SuggestedDatesSelector
                  selectedDates={selectedDates}
                  setSelectedDates={setSelectedDates}
                  isOwnBooking={selectedBooking?.isOwner}
                />
              )}
            </Box>
          }
          positiveText={
            confirmType === "cancel" ? "Yes" : `${titleCase(confirmType)}`
          }
          negativeText={confirmType === "cancel" ? "No" : "Cancel"}
        />
      )}
    </ThemeProvider>
  );
}

export default ListingAppointments;
